/**
 *  Copyright AlpineReplay Inc, 2021. All rights reserved.
 *  Authors: Brian Bal
 */
import Color from 'color'
import Renderer from './Renderer'
import Shape from './Shape'
import { rect, rectCenter, point } from './rect'

const vsSource = `
    attribute vec2 aVertexPosition;

    void main() {
        gl_Position = vec4(aVertexPosition, 0.0, 1.0);
    }	
`

const fsSource = `
    #ifdef GL_ES
    precision highp float;
    #endif

    uniform vec4 uColor;

    void main() {
        gl_FragColor = uColor;
    }
`
   

export default class WebglRenderer extends Renderer {

    getContext(canvas) {
        const ctx = canvas.getContext('webgl')
        if (!ctx) {
            console.error('WebglRenderer.getContext', 'webgl canvas is not supported')
        }
        return ctx
    }

    setupCanvas(canvas) {
        console.log('WebglRenderer.setupCanvas')
        this.size = scaleCanvas(canvas)

        let gl = this.getContext(canvas)
        this.program = initShaderProgram(gl, vsSource, fsSource)

        return this.size
    }

    
    clear(gl) {
        gl.viewport(0, 0, gl.canvas.width, gl.canvas.height)
        gl.clearColor(0.0, 0.0, 0.0, 1.0)  // Clear to black, fully opaque
        gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT)
    }

    draw(gl, shape) {
        //super.draw(ctx, shape)
        
        let cw = gl.canvas.width
        let ch = gl.canvas.height

        let w = (shape.frame.w / cw) * 2
        let h = (shape.frame.h / ch) * 2
        let x = ((shape.frame.x / cw) *2) - 1
        let y = (1 - h) - ((shape.frame.y / ch) *2)

        let tl = point(x, y) 
        let tr = point(x+w, y) 
        let bl = point(x, y+h) 
        let br = point(x+w, y+h) 

        var vertices = new Float32Array([
            tl.x, tl.y,
            tr.x, tr.y,
            bl.x, bl.y,

            bl.x, bl.y,
            br.x, br.y,
            tr.x, tr.y,
        ])
				
        let vbuffer = gl.createBuffer()
        gl.bindBuffer(gl.ARRAY_BUFFER, vbuffer)
        gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STREAM_DRAW)
				
        let itemSize = 2
        let numItems = vertices.length / itemSize

        gl.useProgram(this.program)

        this.program.uColor = gl.getUniformLocation(this.program, 'uColor')
        let color = new Color(shape.props.fill)
        if (color) {
            let colors = color.rgb().round().array()

            gl.uniform4fv(this.program.uColor, [
                colors[0]/255,
                colors[1]/255,
                colors[2]/255,
                1.0,
            ])

        } else {
            gl.uniform4fv(this.program.uColor, [0.0, 1.0, 0.0, 1.0])
        }

        this.program.aVertexPosition = gl.getAttribLocation(this.program, 'aVertexPosition')
        gl.enableVertexAttribArray(this.program.aVertexPosition)
        gl.vertexAttribPointer(this.program.aVertexPosition, itemSize, gl.FLOAT, false, 0, 0)

        gl.drawArrays(gl.TRIANGLES, 0, numItems)
    }

    drawShapeInContext(ctx, shape, adjustedFrame) {
        let frame = adjustedFrame ? adjustedFrame : shape.frame
        switch(shape.type) {
        case Shape.Types.Rectangle:
            drawRectangle(ctx, frame, shape.props)
            break
        case Shape.Types.Ellipse:
            drawEllipse(ctx, frame, shape.props)
            break
        case Shape.Types.Group:
            
            break
        default:
            console.log('CanvasRenderer.drawShapeInContext', 'unknown shape')
        }
    }

    drawFromCache(ctx, shape, cacheEntry) {
        let info = createTextureInfo(ctx, shape.frame, cacheEntry.image)
        drawImage(ctx, this.program, info.texture, info.width, info.height, shape.frame.x, shape.frame.y)
    }

    cacheShape(shape) {
        let tmpCanvas = document.createElement('canvas')
        tmpCanvas.width = shape.frame.w
        tmpCanvas.height = shape.frame.w
        let tmpCtx = tmpCanvas.getContext('2d')

        let aFrame = rect(0, 0, shape.frame.w, shape.frame.h)

        this.drawShapeInContext(tmpCtx, shape, aFrame)

        let image = tmpCtx.getImageData(aFrame.x, aFrame.y, aFrame.w, aFrame.h)
        return image
    }

}

function initShaderProgram(gl, vsSource, fsSource) {
    const vertexShader = loadShader(gl, gl.VERTEX_SHADER, vsSource)
    const fragmentShader = loadShader(gl, gl.FRAGMENT_SHADER, fsSource)

    // Create the shader program
    const shaderProgram = gl.createProgram()
    gl.attachShader(shaderProgram, vertexShader)
    gl.attachShader(shaderProgram, fragmentShader)
    gl.linkProgram(shaderProgram)

    // If creating the shader program failed, alert
    if (!gl.getProgramParameter(shaderProgram, gl.LINK_STATUS)) {
        alert('Unable to initialize the shader program: ' + gl.getProgramInfoLog(shaderProgram))
        return null
    }

    return shaderProgram
}

function loadShader(gl, type, source) {
    const shader = gl.createShader(type)

    // Send the source to the shader object
    gl.shaderSource(shader, source)

    // Compile the shader program
    gl.compileShader(shader)

    // See if it compiled successfully
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        alert('An error occurred compiling the shaders: ' + gl.getShaderInfoLog(shader))
        gl.deleteShader(shader)
        return null
    }

    return shader
}




function createTextureInfo(gl, frame, image) {
    var tex = gl.createTexture()
    gl.bindTexture(gl.TEXTURE_2D, tex)

    // Fill the texture with a 1x1 blue pixel.
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array([0, 0, 255, 255]))

    // let's assume all images are not a power of 2
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)

    var textureInfo = {
        width: frame.w,
        height: frame.h,
        texture: tex,
    }
    gl.bindTexture(gl.TEXTURE_2D, textureInfo.texture)
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image)

    return textureInfo
}

function drawImage(gl, program, tex, texWidth, texHeight, dstX, dstY) {
    gl.bindTexture(gl.TEXTURE_2D, tex)
 
    // Tell WebGL to use our shader program pair
    gl.useProgram(program)

    // Create a buffer.
    var positionBuffer = gl.createBuffer()
    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer)
    var positionLocation = gl.getAttribLocation(program, 'a_position')
    var texcoordLocation = gl.getAttribLocation(program, 'a_texcoord')
    var texcoordBuffer = gl.createBuffer()
    gl.bindBuffer(gl.ARRAY_BUFFER, texcoordBuffer)
    // lookup uniforms
    var matrixLocation = gl.getUniformLocation(program, 'u_matrix')
    var textureLocation = gl.getUniformLocation(program, 'u_texture')
 
    // Setup the attributes to pull data from our buffers
    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer)
    gl.enableVertexAttribArray(positionLocation)
    gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0)
    gl.bindBuffer(gl.ARRAY_BUFFER, texcoordBuffer)
    gl.enableVertexAttribArray(texcoordLocation)
    gl.vertexAttribPointer(texcoordLocation, 2, gl.FLOAT, false, 0, 0)
 
    // this matirx will convert from pixels to clip space
    var matrix = orthographic(0, gl.canvas.width, gl.canvas.height, 0, -1, 1)
 
    // this matrix will translate our quad to dstX, dstY
    matrix = translate(matrix, dstX, dstY, 0)
 
    // this matrix will scale our 1 unit quad
    // from 1 unit to texWidth, texHeight units
    matrix = scale(matrix, texWidth, texHeight, 1)
 
    // Set the matrix.
    gl.uniformMatrix4fv(matrixLocation, false, matrix)
 
    // Tell the shader to get the texture from texture unit 0
    gl.uniform1i(textureLocation, 0)
 
    // draw the quad (2 triangles, 6 vertices)
    gl.drawArrays(gl.TRIANGLES, 0, 6)
}

const scaleCanvas = (canvas) => {
    const size = canvas.getBoundingClientRect()
    const ctx = canvas.getContext('webgl')
    const devicePixelRatio = window.devicePixelRatio || 1
    const backingStoreRatio = (
        ctx.webkitBackingStorePixelRatio ||
        ctx.mozBackingStorePixelRatio ||
        ctx.msBackingStorePixelRatio ||
        ctx.oBackingStorePixelRatio ||
        ctx.backingStorePixelRatio || 1
    )
    const ratio = devicePixelRatio / backingStoreRatio

    if (devicePixelRatio !== backingStoreRatio) {
        canvas.width = size.width * ratio
        canvas.height = size.height * ratio

        canvas.style.width = size.width + 'px'
        canvas.style.height = size.height + 'px'
    } else {
        canvas.width = size.width
        canvas.height = size.height
        canvas.style.width = ''
        canvas.style.height = ''
    } 
    //ctx.scale(ratio, ratio)

    return { scale: ratio, w: size.width, h: size.height }
}

function orthographic(left, right, bottom, top, near, far, dst) {
    dst = dst || new Float32Array(16)

    dst[ 0] = 2 / (right - left)
    dst[ 1] = 0
    dst[ 2] = 0
    dst[ 3] = 0
    dst[ 4] = 0
    dst[ 5] = 2 / (top - bottom)
    dst[ 6] = 0
    dst[ 7] = 0
    dst[ 8] = 0
    dst[ 9] = 0
    dst[10] = 2 / (near - far)
    dst[11] = 0
    dst[12] = (left + right) / (left - right)
    dst[13] = (bottom + top) / (bottom - top)
    dst[14] = (near + far) / (near - far)
    dst[15] = 1

    return dst
}

function translate(m, tx, ty, tz, dst) {
    // This is the optimized version of
    // return multiply(m, translation(tx, ty, tz), dst)
    dst = dst || new Float32Array(16)

    var m00 = m[0]
    var m01 = m[1]
    var m02 = m[2]
    var m03 = m[3]
    var m10 = m[1 * 4 + 0]
    var m11 = m[1 * 4 + 1]
    var m12 = m[1 * 4 + 2]
    var m13 = m[1 * 4 + 3]
    var m20 = m[2 * 4 + 0]
    var m21 = m[2 * 4 + 1]
    var m22 = m[2 * 4 + 2]
    var m23 = m[2 * 4 + 3]
    var m30 = m[3 * 4 + 0]
    var m31 = m[3 * 4 + 1]
    var m32 = m[3 * 4 + 2]
    var m33 = m[3 * 4 + 3]

    if (m !== dst) {
        dst[ 0] = m00
        dst[ 1] = m01
        dst[ 2] = m02
        dst[ 3] = m03
        dst[ 4] = m10
        dst[ 5] = m11
        dst[ 6] = m12
        dst[ 7] = m13
        dst[ 8] = m20
        dst[ 9] = m21
        dst[10] = m22
        dst[11] = m23
    }

    dst[12] = m00 * tx + m10 * ty + m20 * tz + m30
    dst[13] = m01 * tx + m11 * ty + m21 * tz + m31
    dst[14] = m02 * tx + m12 * ty + m22 * tz + m32
    dst[15] = m03 * tx + m13 * ty + m23 * tz + m33

    return dst
}

function scale(m, sx, sy, sz, dst) {
    // This is the optimized verison of
    // return multiply(m, scaling(sx, sy, sz), dst)
    dst = dst || new Float32Array(16)

    dst[ 0] = sx * m[0 * 4 + 0]
    dst[ 1] = sx * m[0 * 4 + 1]
    dst[ 2] = sx * m[0 * 4 + 2]
    dst[ 3] = sx * m[0 * 4 + 3]
    dst[ 4] = sy * m[1 * 4 + 0]
    dst[ 5] = sy * m[1 * 4 + 1]
    dst[ 6] = sy * m[1 * 4 + 2]
    dst[ 7] = sy * m[1 * 4 + 3]
    dst[ 8] = sz * m[2 * 4 + 0]
    dst[ 9] = sz * m[2 * 4 + 1]
    dst[10] = sz * m[2 * 4 + 2]
    dst[11] = sz * m[2 * 4 + 3]

    if (m !== dst) {
        dst[12] = m[12]
        dst[13] = m[13]
        dst[14] = m[14]
        dst[15] = m[15]
    }

    return dst
}

const drawEllipse = (ctx, frame, props) => {

    let c = rectCenter(frame)

    ctx.beginPath()
    ctx.ellipse(c.x, c.y, frame.w/2, frame.h/2, 0, 0, Math.PI*2)
    if (props.fill) {
        ctx.fillStyle = props.fill
        ctx.fill()
    }
    if (props.stroke) {
        ctx.strokeStyle = props.stroke
        ctx.stroke()
    }

}

const drawRectangle = (ctx, frame, props) => {
    //ctx.save()

    ctx.beginPath()
    if (props.fill) {
        ctx.fillStyle = props.fill
        ctx.fillRect(frame.x, frame.y, frame.w, frame.h)
    }
    if (props.stroke) {
        ctx.strokeStyle = props.stroke
        ctx.lineWidth = props.lineWidth
        ctx.strokeRect(frame.x, frame.y, frame.w, frame.h)
    }

    //ctx.restore()
}




